import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  getArchiveStories,
  restoreStory,
  addToHighlightsFromArchive,
  deleteFromArchive,
  getUserHighlights,
} from "../../functions/story";
import ProfileImage from "../../components/ProfileImage";
import StoryViewer from "../../components/stories/StoryViewer";
import "./style.css";

export default function Archive({ token }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [restoreModal, setRestoreModal] = useState(null);
  const [highlightModal, setHighlightModal] = useState(null);
  const [highlightName, setHighlightName] = useState("");
  const [existingHighlightNames, setExistingHighlightNames] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchArchive = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getArchiveStories(token);
      if (res && res.stories) {
        setStories(res.stories);
      }
    } catch (err) {
      console.error("Error fetching archive:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchHighlightNames = async () => {
    try {
      const res = await getUserHighlights(user.id, token);
      if (res && res.highlights) {
        const names = [...new Set(res.highlights.map((s) => s.highlightName).filter(Boolean))];
        setExistingHighlightNames(names);
      }
    } catch (err) {
      console.error("Error fetching highlights:", err);
    }
  };

  useEffect(() => {
    fetchArchive();
  }, [fetchArchive]);

  const getThumbnail = (story) => {
    if (story.image) return `/uploads/stories/${story.image}`;
    if (story.video) return `/uploads/stories/${story.video}`;
    return null;
  };

  // const isExpired = (story) => new Date(story.expiresAt) <= new Date();
  // const isDeleted = (story) => !!story.deletedAt;

  const handleRestore = async (story, duration = 24) => {
    try {
      setActionLoading(true);
      const res = await restoreStory(story._id, token, duration);
      if (res && res.story) {
        setRestoreModal(null);
        fetchArchive();
      } else {
        alert(res?.message || "Failed to restore story.");
      }
    } catch (err) {
      alert("Failed to restore story.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddToHighlights = async () => {
    const name = (highlightName || "").trim();
    if (!name) {
      alert("Please enter a name for your memories.");
      return;
    }
    if (!highlightModal) return;
    try {
      setActionLoading(true);
      const res = await addToHighlightsFromArchive(highlightModal._id, name, token);
      if (res && res.story) {
        setHighlightModal(null);
        setHighlightName("");
        fetchArchive();
      } else {
        alert(res?.message || "Failed to add to Memories.");
      }
    } catch (err) {
      alert("Failed to add to Memories.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePermanent = async (story) => {
    try {
      setActionLoading(true);
      const res = await deleteFromArchive(story._id, token);
      if (res && res.message) {
        setDeleteConfirm(null);
        const viewedStoryId = stories[currentIndex]?._id;
        if (viewedStoryId === story._id) {
          setSelectedStory(null);
        }
        fetchArchive();
      } else {
        alert(res?.message || "Failed to delete.");
      }
    } catch (err) {
      alert("Failed to delete from archive.");
    } finally {
      setActionLoading(false);
    }
  };

  const openHighlightModal = (story) => {
    setHighlightModal(story);
    setHighlightName(story.highlightName || "");
    fetchHighlightNames();
  };

  const openDeleteConfirm = (story) => {
    setDeleteConfirm(story);
  };

  const handleStoryClick = (story) => {
    // Open the full-screen viewer with the entire archive list so the user can
    // navigate (swipe/tap) through all archived vibes like the regular stories
    // viewer, and so the progress bar shows one segment per archived story.
    const idx = stories.findIndex((s) => s._id === story._id);
    setSelectedStory({ user: story.user || user });
    setCurrentIndex(idx >= 0 ? idx : 0);
  };

  const handleCloseViewer = () => setSelectedStory(null);

  const handleNext = (targetIndex) => {
    const next = typeof targetIndex === "number" ? targetIndex : currentIndex + 1;
    if (next < stories.length) {
      setCurrentIndex(next);
    } else {
      handleCloseViewer();
    }
  };

  const handlePrev = (targetIndex) => {
    const prev = typeof targetIndex === "number" ? targetIndex : currentIndex - 1;
    if (prev >= 0) setCurrentIndex(prev);
  };

  if (loading) {
    return (
      <div className="profile_card">
        <div className="profile_card_header">Archive</div>
        <div style={{ padding: "20px", textAlign: "center" }}>Loading archive...</div>
      </div>
    );
  }

  return (
    <div className="profile_card">
      <div className="profile_card_header">Archive</div>
      <p className="archive_description">
        All your vibes are saved here.
      </p>
      {stories.length > 0 ? (
        <div className="archive_grid">
          {stories.map((story) => (
            <div className="archive_item" key={story._id} style={{ position: "relative" }}>
              <div
                className="archive_thumb"
                onClick={() => handleStoryClick(story)}
                style={{
                  backgroundColor:
                    !getThumbnail(story) && story.backgroundColor ? story.backgroundColor : "var(--bg-secondary)",
                }}
              >
                {getThumbnail(story) ? (
                  story.image ? (
                    <ProfileImage
                      src={getThumbnail(story)}
                      alt=""
                      className="archive_thumb_img"
                    />
                  ) : (
                    <video
                      src={getThumbnail(story)}
                      className="archive_thumb_img"
                      muted
                      onMouseEnter={(e) => e.target.play()}
                      onMouseLeave={(e) => {
                        e.target.pause();
                        e.target.currentTime = 0;
                      }}
                    />
                  )
                ) : (
                  <div
                    className="archive_thumb_text"
                    style={{ fontFamily: story.font || "Arial" }}
                  >
                    {story.text ? (story.text.length > 30 ? story.text.slice(0, 30) + "…" : story.text) : "Text"}
                  </div>
                )}
              </div>
              {/* <div className="archive_badges">
                {isExpired(story) && <span className="archive_badge expired">Expired</span>}
                {isDeleted(story) && <span className="archive_badge deleted">Removed</span>}
                {story.savedToHighlights && (
                  <span className="archive_badge highlight">{story.highlightName || "Memories"}</span>
                )}
              </div> */}
            </div>
          ))}
        </div>
      ) : (
        <div className="no_highlights" style={{ padding: "40px 20px", textAlign: "center", color: "var(--color-secondary)" }}>
          No vibes in your archive yet.
        </div>
      )}

      {/* Restore modal */}
      {restoreModal && (
        <div className="edit_cover_modal_overlay" onClick={() => setRestoreModal(null)}>
          <div className="edit_cover_modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit_cover_header">
              <h3>Restore to Stories</h3>
              <button type="button" onClick={() => setRestoreModal(null)} className="close_btn">✕</button>
            </div>
            <div className="edit_cover_content">
              <p>Show this story again for:</p>
              <div className="archive_restore_duration">
                {[168, 72, 48, 24, 12, 6, 1].map((h) => (
                  <button
                    key={h}
                    type="button"
                    className="archive_duration_btn"
                    onClick={() => handleRestore(restoreModal, h)}
                    disabled={actionLoading}
                  >
                    {h >= 24 && h % 24 === 0 ? `${h / 24}d` : `${h}h`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add to Memories modal */}
      {highlightModal && (
        <div className="edit_cover_modal_overlay" onClick={() => { setHighlightModal(null); setHighlightName(""); }}>
          <div className="edit_cover_modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit_cover_header">
              <h3>Add to Memories</h3>
              <button type="button" onClick={() => { setHighlightModal(null); setHighlightName(""); }} className="close_btn">✕</button>
            </div>
            <div className="edit_cover_content">
              <label>Memories name</label>
              <input
                type="text"
                value={highlightName}
                onChange={(e) => setHighlightName(e.target.value)}
                placeholder="e.g. Travel"
                maxLength={50}
                className="archive_highlight_input"
              />
              {existingHighlightNames.length > 0 && (
                <div className="archive_existing_highlights">
                  <span>Or choose existing:</span>
                  <div className="archive_highlight_chips">
                    {existingHighlightNames.map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="archive_highlight_chip"
                        onClick={() => setHighlightName(name)}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="archive_modal_actions">
                <button type="button" className="cancel_btn" onClick={() => { setHighlightModal(null); setHighlightName(""); }}>
                  Cancel
                </button>
                <button type="button" className="confirm_btn" onClick={handleAddToHighlights} disabled={actionLoading || !highlightName.trim()}>
                  {actionLoading ? "Adding…" : "Add"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedStory && (
        <StoryViewer
          userStories={{ user: selectedStory.user, stories }}
          currentIndex={currentIndex}
          onClose={handleCloseViewer}
          onNext={handleNext}
          onPrev={handlePrev}
          user={user}
          isHighlight={false}
          isArchive={true}
          onStoryDeleted={fetchArchive}
          onArchiveAddToHighlights={openHighlightModal}
          onArchiveDelete={openDeleteConfirm}
        />
      )}

      {/* Delete confirmation — portal above StoryViewer / profile_top stacking */}
      {deleteConfirm &&
        createPortal(
          <div
            className="delete_confirm_overlay archive_story_delete_overlay"
            onClick={() => setDeleteConfirm(null)}
            role="presentation"
          >
            <div
              className="delete_confirm_modal archive_story_delete_modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="archive-delete-title"
            >
              <h3 id="archive-delete-title">Delete from Archive?</h3>
              <p>
               This vibe will be permanently removed from your archive.
              </p>
              <div className="delete_confirm_actions">
                <button type="button" className="cancel_btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button type="button" className="delete_btn" onClick={() => handleDeletePermanent(deleteConfirm)} disabled={actionLoading}>
                  {actionLoading ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
